/**
 * Environment gates for the GA4 module.
 *
 * Kept separate from ga4Tracker/ga4Consent so both can share them without an
 * import cycle (ga4Tracker already imports ga4Consent).
 */

import {
  APP_ENV_ENV,
  GA4_DEBUG_MODE_ENV,
  GA4_FORCE_CONSENT_ENV,
} from './ga4Constants';

const isEnvTrue = (name: string): boolean =>
  String(process.env[name] || '').toLowerCase() === 'true';

/** True only under `npm start`; every `react-scripts build` reports 'production'. */
const isDevServer = (): boolean => process.env.NODE_ENV === 'development';

export const isProductionEnv = (): boolean =>
  String(process.env[APP_ENV_ENV] || '').toLowerCase() === 'production';

/** CookieYes is domain-bound and generally does not run on localhost. */
export const isLocalhostDevServer = (): boolean => {
  if (!isDevServer()) return false;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
};

/**
 * Gate for the QA overrides.
 *
 * REACT_APP_* values are inlined at build time, so the realistic way one of
 * these reaches production is a copied env file rather than a deliberate
 * decision. Refusing here makes that structurally impossible instead of
 * relying on deployment convention.
 */
const isNonProdOverrideEnabled = (name: string): boolean => {
  if (isProductionEnv()) return false;
  return isEnvTrue(name);
};

/**
 * Adds `debug_mode` so events surface in GA4 DebugView. On by default for the
 * dev server; on deployed non-production hosts it needs REACT_APP_GA4_DEBUG_MODE.
 */
export const isGa4DebugMode = (): boolean =>
  isDevServer() || isNonProdOverrideEnabled(GA4_DEBUG_MODE_ENV);

/**
 * Whether to grant Consent Mode without a banner.
 *
 * Consent Mode is page-global with no per-property scoping, so this also
 * unblocks legacy G-V03TEP7J74 and any Google tag GTM loads — acceptable on QA
 * traffic, a consent-bypass on real users. Hence the production refusal above.
 */
export const shouldGrantConsentWithoutBanner = (): boolean =>
  isLocalhostDevServer() || isNonProdOverrideEnabled(GA4_FORCE_CONSENT_ENV);
